import { useState, type FormEvent } from "react";
import { useCreateOrg, useCreateTeam, useSpaces } from "../api/hooks";
import { useActiveSpace } from "../api/space-context";
import { Drawer } from "./Drawer";

// Cria uma organização ou um time. Time pode ser vinculado a uma org que o
// usuário já participa, ou ficar sem org (cross-org). Ao criar, torna o novo
// espaço o ativo.

const inputCls =
  "w-full rounded-md border border-slate-300 bg-white px-3 py-1.5 text-sm text-slate-900 placeholder:text-slate-400 focus:border-primary focus:outline-none focus:ring-1 focus:ring-primary dark:border-slate-700 dark:bg-slate-900 dark:text-slate-100 dark:placeholder:text-slate-500";

export function CreateSpaceDrawer({ kind, onClose }: { kind: "org" | "team"; onClose: () => void }) {
  const [name, setName] = useState("");
  const [orgId, setOrgId] = useState<number | null>(null);
  const createOrg = useCreateOrg();
  const createTeam = useCreateTeam();
  const spaces = useSpaces();
  const { setActiveSpace } = useActiveSpace();
  const pending = createOrg.isPending || createTeam.isPending;

  const onSubmit = (e: FormEvent) => {
    e.preventDefault();
    const n = name.trim();
    if (!n) return;
    if (kind === "org") {
      createOrg.mutate(
        { name: n },
        { onSuccess: (org) => { setActiveSpace({ type: "org", id: org.id }); onClose(); } },
      );
    } else {
      createTeam.mutate(
        { name: n, orgId },
        { onSuccess: (team) => { setActiveSpace({ type: "team", id: team.id }); onClose(); } },
      );
    }
  };

  const err = (createOrg.error ?? createTeam.error) as Error | null;

  return (
    <Drawer open title={kind === "org" ? "Nova organização" : "Novo time"} onClose={onClose}>
      <form onSubmit={onSubmit} className="space-y-4">
        <label className="block text-sm">
          <span className="mb-1 block font-medium text-slate-700 dark:text-slate-300">Nome</span>
          <input
            value={name}
            onChange={(e) => setName(e.target.value)}
            placeholder={kind === "org" ? "Ex.: ETUS" : "Ex.: Squad Produto"}
            className={inputCls}
            autoFocus
          />
        </label>

        {kind === "team" && (
          <label className="block text-sm">
            <span className="mb-1 block font-medium text-slate-700 dark:text-slate-300">
              Organização (opcional)
            </span>
            <select
              value={orgId ?? ""}
              onChange={(e) => setOrgId(e.target.value ? Number(e.target.value) : null)}
              className="w-full rounded-md border border-slate-300 bg-white px-3 py-1.5 text-sm text-slate-900 focus:border-primary focus:outline-none focus:ring-1 focus:ring-primary dark:border-slate-700 dark:bg-slate-900 dark:text-slate-100"
            >
              <option value="">Sem organização (cross-org)</option>
              {spaces.data?.orgs.map((o) => (
                <option key={o.id} value={o.id}>
                  {o.name}
                </option>
              ))}
            </select>
            <p className="mt-1 text-xs text-slate-400 dark:text-slate-500">
              Um time sem organização pode ter membros de qualquer lugar.
            </p>
          </label>
        )}

        <div className="flex justify-end gap-2">
          <button
            type="button"
            onClick={onClose}
            className="rounded-md border border-slate-300 px-3 py-1.5 text-sm font-medium text-slate-700 transition-colors hover:bg-slate-50 dark:border-slate-700 dark:text-slate-300 dark:hover:bg-slate-800"
          >
            Cancelar
          </button>
          <button
            type="submit"
            disabled={pending || name.trim().length === 0}
            className="rounded-md bg-primary px-3 py-1.5 text-sm font-medium text-white transition-colors hover:bg-primary-dark disabled:opacity-50"
          >
            Criar
          </button>
        </div>
        {err && <p className="text-sm text-red-600 dark:text-red-400">Erro: {err.message}</p>}
      </form>
    </Drawer>
  );
}
